import React from "react";
import { Formik as FormikBase, Field } from "formik";
import { Link } from "react-router-dom";
import { InputAdornment, Typography } from "@mui/material";
import { EmailOutlined, LockOutlined } from "@mui/icons-material";

import {
  emailMDInputFormik,
  passwordMDInputFormik,
} from "@/helpers/formInputs";
import LoadingButton from "@/components/loaders/LoadingButton";
import LeftView from "@/features/auth/components/LeftView";
import useQuery from "@/helpers/hooks/useQuery";
import { IS_HYPER_LOGIN_PAGE } from "@/config/appConfig";
import MainSEOBase from "@/components/seo/MainSEO";
import MaintenanceBanner from "./MaintenanceBanner";
import { useAppTheme } from "@/helpers/contexts/themeContext";
import { getImage } from "@/helpers/utils";
import { useLogin } from "../hooks/useLogin";
import { validateLogin } from "../validators/authValidators";
import { ROUTES } from "@/helpers/routes/routePaths";

const Formik = FormikBase as unknown as React.ComponentType<any>;
const MainSEO = MainSEOBase as unknown as React.ComponentType<any>;

const LoginForm = () => {
  const { email, subscribe } = useQuery();
  const { loginData } = useAppTheme() as any;
  const { login, isLoading, maintenance } = useLogin(email, subscribe);

  const onSubmitForm = async (values: any, { setSubmitting }: any) => {
    await login(values);
    setSubmitting(false);
  };

  if (maintenance) {
    return <MaintenanceBanner />;
  }

  return (
    <div className="row login-page">
      <MainSEO title="Login" />
      <div className="col-lg-6 p-0 d-none d-lg-block">
        <LeftView />
      </div>
      <div className="col-lg-6 px-lg-5 py-5">
        <div className="d-flex flex-column justify-content-center h-100">
          <div className="mb-5 pb-3">
            {IS_HYPER_LOGIN_PAGE ? (
              <img src="/img/login/logo.png" alt="logo" />
            ) : (
              loginData?.logo && (
                <img
                  src={getImage(loginData?.logo, "BRAND")}
                  style={{
                    maxWidth: "200px",
                    maxHeight: "70px",
                    objectFit: "contain",
                  }}
                  alt="logo"
                />
              )
            )}
          </div>

          <div className="login-form">
            <Typography
              component="h1"
              variant="h4"
              className="fs-39"
              gutterBottom
            >
              Welcome Back!
            </Typography>
            <Typography variant="body1" component="h2" className="fs-20">
              Promote your business like a pro in just a few clicks!
            </Typography>
            <Formik
              initialValues={{ email: email || "", password: "" }}
              onSubmit={onSubmitForm}
              validate={validateLogin}
            >
              {({ handleSubmit }: any) => {
                return (
                  <form
                    onSubmit={handleSubmit}
                    onBlurCapture={(event) => {
                      // Avoid moving a navigation link between pointer down and click.
                      if (
                        event.relatedTarget instanceof Element &&
                        event.relatedTarget.closest("a[href]")
                      ) {
                        event.stopPropagation();
                      }
                    }}
                    className="mt-1 mt-sm-4 mt-md-5"
                  >
                    <Field
                      name="email"
                      label="E-Mail"
                      component={emailMDInputFormik}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined color="disabled" />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Enter your email"
                    />
                    <Field
                      name="password"
                      label="Password"
                      component={passwordMDInputFormik}
                      autoComplete="new-password"
                      autoFocus={email ? true : false}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined color="disabled" />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Enter your password"
                    />
                    <div className="row py-0 pt-0 pt-sm-2">
                      <div className="col-xl-6 col-md-12 col-sm-6 mb-3">
                        <Link
                          to={ROUTES.FORGOT_PASSWORD}
                          className={`heading9 link link-w ${
                            isLoading || maintenance ? "disabled-link" : ""
                          }`}
                        >
                          Forgot Password?
                        </Link>
                      </div>
                      <div className="col-xl-6 col-md-12 col-sm-6 mb-3">
                        <div className="heading9 font-weight-normal d-flex align-items-center justify-content-end">
                          Your brand works with us?
                          <Link
                            to={ROUTES.SIGNUP}
                            className={`heading9 ml-1 link link-w ${
                              isLoading || maintenance ? "disabled-link" : ""
                            }`}
                          >
                            Signup
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="row justify-content-center justify-content-sm-start mt-3 mt-sm-4">
                      <div className="col-sm-6 col-xl-4">
                        <LoadingButton
                          type="submit"
                          text="Login"
                          isLoading={isLoading}
                          fullWidth
                        />
                      </div>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
